/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Grid, InputLabel } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'

import { Container, Page } from 'components'
import {
  ExportFormatDialog,
  Header,
  ReportVersionTable,
  PartnerForm,
  PartnerAuditForm,
  PartnerReportSelection,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import useStyles from './styles'
import constants from 'constants/index'
import schema from './components/PartnerForm/schema'
import helpers from 'helpers'

const PartnerReport = () => {
  const [exporting, setExporting] = useState(false)
  const [refresher, setRefresher] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [currentReportVersion, setCurrentReportVersion] = useState()

  const classes = useStyles()
  const snackbar = useSnackbar()
  const { company } = useAuth()

  const { DESCRIPTION, REPORT_TYPES, EXPORT_FORMAT_TYPES } = constants.reports

  const formMethods = useForm({
    validationSchema: schema(),
    defaultValues: {
      exportFormat: EXPORT_FORMAT_TYPES.PDF,
      dataAction: constants.reportVersions.DESTROY_VERSION_ID_ACTION,
    },
  })

  const {
    control,
    watch,
    errors,
    clearError,
    setError,
    triggerValidation,
    setValue,
  } = formMethods

  const reportSelected = watch('reportSelected')
  const partnerIdField = watch('partnerId')
  const auditIdField = watch('auditId')

  const isAssessment = [
    REPORT_TYPES.SINGLE_ASSESSMENT,
    REPORT_TYPES.ALL_ASSESSMENTS,
  ].includes(reportSelected)
  const isPartner = [
    REPORT_TYPES.SINGLE_PARTNER,
    REPORT_TYPES.ALL_PARTNERS,
  ].includes(reportSelected)
  const isReportByAll = [
    REPORT_TYPES.ALL_ASSESSMENTS,
    REPORT_TYPES.ALL_PARTNERS,
  ].includes(reportSelected)

  const { response, isLoading: loadingSuppliers } = useFetch(
    service.dponet.companies.indexSupplierReport,
    {
      companyId: company.id,
    },
    [],
  )

  const companySuppliers = response?.data?.companySuppliers

  const partnersOptions = useMemo(
    () =>
      (companySuppliers || [])
        .filter((companySupplier) => {
          if (isAssessment) {
            return companySupplier.preAvaliation === true
          }
          if (isPartner) {
            return !companySupplier.preAvaliation
          }
          return true
        })
        .map((companySupplier) => {
          const { id, name, document } = companySupplier.supplier
          const displayName =
            companySupplier.displayName ||
            name ||
            '[Razão Social não informada]'

          return {
            id,
            companySupplier,
            name: `${displayName} - ${document}`,
          }
        }),
    [companySuppliers, isAssessment, isPartner],
  )

  const handleRefresh = () => setRefresher((prev) => !prev)

  const mixPanel = (action) => {
    helpers.mixpanel.track('Relatório de Fornecedores', 'Gerar Relatório', {
      action,
    })
  }

  const triggerMixPanelAction = () => {
    if (reportSelected === REPORT_TYPES.ALL_PARTNERS) {
      mixPanel('gerar-relatorio-fornecedores')
    } else if (reportSelected === REPORT_TYPES.SINGLE_PARTNER) {
      mixPanel('gerar-relatorio-fornecedor')
    } else if (reportSelected === REPORT_TYPES.ALL_ASSESSMENTS) {
      mixPanel('gerar-relatorio-pre-avaliacoes')
    } else {
      mixPanel('gerar-relatorio-pre-avaliacao')
    }
  }

  const buildExportParams = (shouldCreateVersion, exportFormat) => {
    const selectedPartners = isReportByAll
      ? partnersOptions
      : partnersOptions.filter((partner) => partner.id === partnerIdField?.id)

    return {
      partnerIds: selectedPartners.map((partner) => partner.id),
      companySupplierAuditId: auditIdField?.id,
      reportType: reportSelected,
      exportFormat,
      shouldCreateVersion,
    }
  }

  const exportReport = async (shouldCreateVersion, exportFormat) => {
    setExporting(true)
    try {
      triggerMixPanelAction()

      await service.dponet.suppliers.partnerExportReport(
        buildExportParams(shouldCreateVersion, exportFormat),
      )

      const isExcel = exportFormat === EXPORT_FORMAT_TYPES.EXCEL

      snackbar.open({
        message: `O relatório ${
          isExcel ? 'Excel ' : ''
        }será gerado e enviado ao seu e-mail. Pode demorar alguns minutos.`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            'Houve um erro durante a criação do relatório.',
        ),
        variant: 'error',
      })
    } finally {
      setExporting(false)
      setTimeout(handleRefresh, 8000)
    }
  }

  const handleCloseExportFormatDialog = () => setDialogOpen(false)

  const handleOpenExportFormatDialog = async () => {
    if (loadingSuppliers) return

    if (isEmpty(partnersOptions)) {
      snackbar.open({
        message: isAssessment
          ? 'Não existem pré-avaliados disponíveis para gerar esse relatório'
          : 'Não existem fornecedores disponíveis para gerar esse relatório',
        variant: 'error',
      })
      return
    }

    const isValid = await triggerValidation()
    if (!isValid) return

    setDialogOpen(true)
  }

  const onConfirmExport = async (data) => {
    const hasVersion = !!currentReportVersion?.id

    if (hasVersion && !data?.dataAction) {
      setError(
        'dataAction',
        'required',
        'Selecione se deseja apenas baixar ou gerar uma nova versão.',
      )
      return
    }
    clearError('dataAction')
    handleCloseExportFormatDialog()

    const isDestroyAction =
      data?.dataAction === constants.reportVersions.DESTROY_VERSION_ID_ACTION

    if (isDestroyAction && hasVersion) {
      try {
        await service.dponet.reportVersions.destroy({
          reportVersionId: currentReportVersion.id,
        })
        setCurrentReportVersion(null)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error ||
              'Falha ao remover versão do relatório.',
          ),
          variant: 'error',
        })
      }
    }

    const shouldCreateVersion = !hasVersion || isDestroyAction

    await exportReport(shouldCreateVersion, data.exportFormat)
  }

  useEffect(() => {
    if (partnerIdField) {
      setValue('partnerId', null)
    }
    if (auditIdField) {
      setValue('auditId', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSelected])

  return (
    <FormContext {...formMethods}>
      <Page className={classes.root} title="Relatório de Fornecedores">
        <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
          <Box py={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Header
                  type="partner"
                  reportTour="partner"
                  isLoading={loadingSuppliers}
                  buttonAction={handleOpenExportFormatDialog}
                  disabled={exporting}
                  title={DESCRIPTION.partner.title}
                  description={DESCRIPTION.partner.content}
                  buttonType="button"
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel className={classes.label}>
                  Escolha a versão do relatório:
                </InputLabel>
                <PartnerReportSelection
                  control={control}
                  errors={errors}
                  clearError={clearError}
                />
              </Grid>
              {!isReportByAll &&
                !loadingSuppliers &&
                !isEmpty(partnersOptions) && (
                  <>
                    <Grid item xs={12}>
                      <PartnerForm
                        control={control}
                        errors={errors}
                        partnersOptions={partnersOptions}
                        label={
                          reportSelected === 'reportByAssessment'
                            ? 'Pré-Avaliado'
                            : 'Fornecedor'
                        }
                      />
                    </Grid>
                    {partnerIdField?.id && (
                      <Grid item xs={12}>
                        <PartnerAuditForm
                          key={partnerIdField.id}
                          control={control}
                          errors={errors}
                          companySupplier={partnerIdField.companySupplier}
                          setValue={setValue}
                        />
                      </Grid>
                    )}
                  </>
                )}
              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.PARTNER}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
          </Box>

          <ExportFormatDialog
            open={dialogOpen}
            onClose={handleCloseExportFormatDialog}
            onConfirmExport={formMethods.handleSubmit(onConfirmExport)}
            isLoadingExcel={exporting}
            currentReportVersion={currentReportVersion}
          />
        </Container>
      </Page>
    </FormContext>
  )
}
export default PartnerReport
